import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'react-string-format'
import moment from 'moment'
import { Template } from 'devextreme-react/core/template'
import { Icon, Popover, Position, PopoverInteractionKind, Menu, MenuItem, Tooltip } from '@blueprintjs/core'
import DataGrid, { Column, FilterRow, SearchPanel, Paging, Pager, Scrolling, Export, Summary, TotalItem } from 'devextreme-react/data-grid'
/* API LIST */
import { getOrderConfirmationDetail } from '../../api/order'
/* CUSTOM HOOKS */
import useAlert from '../../lib/useAlert'
import { userHasMenu } from 'lib/userHasMenu'
import { isPermission } from 'lib/usePermission'
/* COMPONENTS */
import BPDialogV2 from 'components/BPDialogV2'
import ToolbarExportButton from 'components/DevExpressGrid/ToolbarExportButton'
import InvoiceShortcuts from 'views/Shortcuts/Invoices'
import DeliveryShortcuts from 'views/Shortcuts/Deliveries'
const OrderConfirmationDetail = (props) => {
  const { t } = useTranslation()
  const { openAlert } = useAlert()
  const [values, setValues] = useState([])
  const [materialNumber, setMaterialNumber] = useState()
  const refGrid = useRef()
  const refDeliveryShortcutsDialog = useRef()
  const refInvoiceShortcutsDialog = useRef()

  const generateExportFileName = format(
    t('export_filename_order_confirmation_detail'),
    values.sap_order_number,
    moment().format('DD.MM.YYYY'),
  )
  const priceColumnFormat = { type: 'fixedPoint', precision: 2 }

  const getConfirmationDetails = async () => {
    try {
      if (props.documentNo) {
        const { data, status } = await getOrderConfirmationDetail(props.documentNo)
        if (data && status === 200 && data.P_RESULT_CODE === 0) {
          const result = JSON.parse(data.RESULT[0].data)
          const newRows = result.rows.map((rw, index) => ({ ...rw, itemIndex: index + 1 }))
          // console.log(newRows)
          result.rows = newRows
          setValues(result)
        } else {
          openAlert('error', true, data.P_RESULT_MESSAGE)
        }
      }
    } catch (error) {
      openAlert('error', true, error.message)
    }
  }
  const cbGetConfirmationDetails = useCallback(getConfirmationDetails, [props.documentNo])

  const menuCellShortcuts = ({ data }) => {
    return (
      <Tooltip popoverClassName='blueprintTooltip' content={t('shortcuts')} position={Position.BOTTOM}>
        <Popover
          position={Position.BOTTOM_LEFT}
          content={popoverContentShortcuts(data)}
          target={<Icon icon='share' className='bp3-grid-icon-shortcut' />}
          interactionKind={PopoverInteractionKind.CLICK}
        />
      </Tooltip>
    )
  }
  const popoverContentShortcuts = (data) => {
    return (
      <Menu>
        {userHasMenu(['delivery']) && (
          <MenuItem
            icon='tractor'
            text={t('title_delivery_shortcuts')}
            onClick={() => {
              setMaterialNumber(data.material)
              refDeliveryShortcutsDialog.current.show()
            }}
          />
        )}
        {userHasMenu(['invoice']) && (
          <MenuItem
            icon='document'
            text={t('title_invoice_shortcuts')}
            onClick={() => {
              setMaterialNumber(data.material)
              refInvoiceShortcutsDialog.current.show()
            }}
          />
        )}
      </Menu>
    )
  }

  useEffect(() => {
    cbGetConfirmationDetails()
  }, [cbGetConfirmationDetails])

  return (
    <>
      <BPDialogV2
        ref={refDeliveryShortcutsDialog}
        size='90'
        removeFooter
        title={t('page_title_delivery_shortcuts')}
        onClose={() => {
          refDeliveryShortcutsDialog.current.hide()
        }}
      >
        <DeliveryShortcuts sap_order_number={values.sap_order_number} material={materialNumber} />
      </BPDialogV2>
      <BPDialogV2
        ref={refInvoiceShortcutsDialog}
        size='90'
        removeFooter
        title={t('page_title_invoice_shortcuts')}
        onClose={() => {
          refInvoiceShortcutsDialog.current.hide()
        }}
      >
        <InvoiceShortcuts sap_order_number={values.sap_order_number} material={materialNumber} />
      </BPDialogV2>
      <div id='panel'>
        <div className='alert alert-success'>
          <div className='row'>
            <div className='col-md-6 col-sm-12'>
              <div className='row'>
                <div className='col-md-3 col-sm-6'>
                  <strong>{t('document_no')}</strong>
                </div>
                <div className='col-md-9 col-sm-6'>: {values.sap_order_number}</div>
              </div>
              <div className='row'>
                <div className='col-md-3 col-sm-6'>
                  <strong>{t('date')}</strong>
                </div>
                <div className='col-md-9 col-sm-6'>: {values?.doc_date ? moment(values.doc_date).format('DD.MM.YYYY') : ''}</div>
              </div>

              <div className='row'>
                <div className='col-md-3 col-sm-6'>
                  <strong>{t('customer')}</strong>
                </div>
                <div className='col-md-9 col-sm-6'>: {values.custdesc}</div>
              </div>
              <div className='row'>
                <div className='col-md-3 col-sm-6'>
                  <strong>{t('shipping_address')}</strong>
                </div>
                <div className='col-md-9 col-sm-6'>: {values.shipto}</div>
              </div>
            </div>
            <div className='col-md-6 col-sm-12'>
              <div className='row'>
                <div className='col-md-3 col-sm-6'>
                  <strong>{t('web_order_number')}</strong>
                </div>
                <div className='col-md-9 col-sm-6'>: {values.webshop_number}</div>
              </div>
              <div className='row'>
                <div className='col-md-3 col-sm-6'>
                  <strong>{t('delivery_date')}</strong>
                </div>
                <div className='col-md-9 col-sm-6'>: {values?.del_date ? moment(values.del_date).format('DD.MM.YYYY') : ''}</div>
              </div>
              <div className='row align-items-center'>
                <div className='col-md-3 col-sm-6'>
                  <strong>{t('requested_shipment_date')}</strong>
                </div>
                <div className='col-md-9 col-sm-6'>
                  : {values?.requested_shipment_date ? moment(values.requested_shipment_date).format('DD.MM.YYYY') : ''}
                </div>
              </div>
              <div className='row'>
                <div className='col-md-3 col-sm-6'>
                  <strong>{t('description')}</strong>
                </div>
                <div className='col-md-9 col-sm-6'>: {values.header_text}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id='gridContainer'>
        <DataGrid
          ref={refGrid}
          dataSource={values.rows}
          onToolbarPreparing={onToolbarPreparing}
          allowColumnReordering
          showBorders
          selection={{ mode: 'single' }}
          hoverStateEnabled
          keyExpr='refid'
          showRowLines
          columnAutoWidth
          rowAlternationEnabled
          focusedRowEnabled
          loadPanel={{ enabled: false }}
          width='100%'
          onContentReady={(e) => {
            e.component.option('height', document.getElementsByClassName('bp3-dialog')[0].clientHeight - 170)
          }}
          allowColumnResizing
          columnResizingMode='widget'
        >
          <Export enabled={false} fileName={generateExportFileName} allowExportSelectedData={false} />
          <FilterRow visible />
          <SearchPanel width={240} placeholder={t('search')} visible searchVisibleColumnsOnly />
          <Paging enabled={false} defaultPageSize={20} />
          <Pager
            showPageSizeSelector
            allowedPageSizes={[20, 50, 100]}
            showNavigationButtons
            showInfo
            infoText={t('pager_info_text')}
            visible={false}
          />
          <Scrolling mode='standart' />
          <Column dataField='itemIndex' caption={t('line')} alignment='center' />
          {userHasMenu(['invoice', 'delivery']) && (
            <Column
              cellRender={menuCellShortcuts}
              width={36}
              allowSorting={false}
              alignment='center'
              allowReordering={false}
              allowExporting={false}
              name='colShortcuts'
            />
          )}
          <Column dataField='position' caption={t('pos')} />
          <Column dataField='contractdesc' caption={t('contract')} />
          <Column dataField='material' caption={t('mat_nr')} />
          <Column dataField='mat_desc' caption={t('material')} />
          <Column dataField='batch' caption={t('batch')}  visible={isPermission('siparis_satirlarinin_lot_miad_bilgisini_gorebilir')}/>
          <Column dataField='expiry_date' dataType='date' caption={t('exp_date')} format='dd.MM.yyyy' width={120} alignment='center'  visible={isPermission('siparis_satirlarinin_lot_miad_bilgisini_gorebilir')}/>
          <Column dataField='quantity' caption={t('qty')} width={80} />
          <Column dataField='netprice_unit' caption={t('unit_price')} format={priceColumnFormat} />
          <Column dataField='netprice_total' caption={t('total')} format={priceColumnFormat} />
          <Column dataField='item_text' caption={t('line_exp')} width={80} />
          <Summary>
            <TotalItem column='itemIndex' summaryType='count' displayFormat={t('count') + ': {0}'} />
            <TotalItem column='netprice_total' summaryType='sum' valueFormat={priceColumnFormat} displayFormat={t('sum') + ': {0}'} />
          </Summary>
          <Template
            name='exportButton'
            render={() => (
              <ToolbarExportButton
                onClick={() => {
                  refGrid.current._instance.exportToExcel(false)
                }}
              />
            )}
          />
        </DataGrid>
      </div>
    </>
  )
}

const onToolbarPreparing = (e) => {
  e.toolbarOptions.items.unshift({
    location: 'after',
    template: 'exportButton',
  })
}

export default OrderConfirmationDetail
